/* QSPEC Calm hero — scroll-scrubbed image sequence
 * Memory model: frames are fetched as compressed Blobs (~50KB each) and decoded to
 * ImageBitmap only inside a window around the playhead (at most ±3). Bitmaps outside
 * the window are closed, including when the page is hidden.
 */
(function () {
  'use strict';

  function init(root) {
    var cfg;
    try { cfg = JSON.parse(root.querySelector('[data-calm-config]').textContent); }
    catch (e) { root.setAttribute('data-calm-mode', 'static'); return; }
    var canvas = root.querySelector('canvas[data-calm-canvas]');
    var poster = root.querySelector('[data-calm-poster]');
    var stage = root.querySelector('[data-calm-stage]');
    if (!canvas || !cfg.urls || !cfg.urls.length) return;

    var mq = window.matchMedia ? window.matchMedia.bind(window) : null;
    var motionQuery = mq ? mq('(prefers-reduced-motion: reduce)') : null;
    var mobileQuery = mq ? mq('(max-width: 767px)') : null;
    var connection = navigator.connection;
    var reduced = motionQuery && motionQuery.matches;
    var saveData = connection && connection.saveData;
    var slow = connection && /(^|-)2g$/.test(connection.effectiveType || '');
    var isMobile = !!(mobileQuery && mobileQuery.matches);
    var urls = (isMobile && cfg.urlsMobile && cfg.urlsMobile.length) ? cfg.urlsMobile : cfg.urls;
    var frameW = isMobile && cfg.mobileWidth ? cfg.mobileWidth : cfg.width;
    var frameH = isMobile && cfg.mobileHeight ? cfg.mobileHeight : cfg.height;
    var hasGsap = window.gsap && window.ScrollTrigger;
    var canBitmap = typeof window.createImageBitmap === 'function';
    var stageEls = root.querySelectorAll('.calm-hero__stage');
    var stage0El = root.querySelector('.calm-hero__stage[data-stage="0"]');
    var taglineEl = root.querySelector('.calm-hero__tagline');
    var cleanupSequence = null, disposed = false;

    function resetVisual() {
      if (poster) poster.hidden = false;
      root.removeAttribute('data-calm-ready');
      root.style.removeProperty('--calm-progress');
      root.style.removeProperty('--calm-hold');
      if (stage) stage.setAttribute('data-calm-stage', '0');
      stageEls.forEach(function (el) {
        el.removeAttribute('aria-hidden');
        if ('inert' in el) el.inert = false;
        el.style.removeProperty('opacity');
        el.style.removeProperty('pointer-events');
      });
      if (taglineEl) taglineEl.style.removeProperty('opacity');
    }
    function listen(target, add) {
      if (!target) return;
      if (target.addEventListener) target[add ? 'addEventListener' : 'removeEventListener']('change', restart);
      else if (target.addListener) target[add ? 'addListener' : 'removeListener'](restart);
    }
    function restart() {
      if (disposed) return;
      destroy(); root._calmInit = true; init(root);
      if (window.ScrollTrigger) window.ScrollTrigger.refresh();
    }
    function destroy() {
      if (disposed) return;
      disposed = true;
      if (cleanupSequence) cleanupSequence();
      listen(motionQuery, false); listen(mobileQuery, false); listen(connection, false);
      resetVisual();
      root.removeAttribute('data-calm-mode');
      root.removeAttribute('data-calm-fallback');
      root._calmInit = false; delete root._calm;
    }
    function fallback(reason) {
      if (disposed) return;
      if (cleanupSequence) { cleanupSequence(); cleanupSequence = null; }
      resetVisual();
      root.setAttribute('data-calm-mode', 'static');
      root.setAttribute('data-calm-fallback', reason);
      if (window.ScrollTrigger) window.ScrollTrigger.refresh();
    }
    root._calm = { destroy: destroy };
    listen(motionQuery, true); listen(mobileQuery, true); listen(connection, true);
    resetVisual();
    if (!hasGsap || (!cfg.forceSequence && (reduced || saveData || slow))) {
      fallback(hasGsap ? 'preference' : 'animation-unavailable'); return;
    }
    var ctx;
    try { ctx = canvas.getContext('2d', { alpha: false }); } catch (e) {}
    if (!ctx) { fallback('canvas-unavailable'); return; }
    window.gsap.registerPlugin(window.ScrollTrigger);
    var destroyed = false;
    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    var fetchOpts = controller ? { signal: controller.signal } : undefined;
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var total = urls.length;
    var blobs = new Array(total);     // compressed
    var bitmaps = new Array(total);   // decoded (window only)
    var decoding = new Array(total);
    var pendingDecodes = 0;
    var playhead = { frame: 0 };
    var current = -1;
    var WINDOW = Math.max(1, Math.min(cfg.decodeWindow || 3, 3)); // at most 7 decoded frames (~58 MB at 1920×1080)

    var cssW = 0, cssH = 0; // canvas CSS size, cached from ResizeObserver so draw() never forces layout
    function measure() { var r = canvas.getBoundingClientRect(); cssW = r.width; cssH = r.height; }
    function ensureSize() {
      if (!cssW || !cssH) measure();
      var w = Math.max(1, Math.round(cssW * dpr));
      var h = Math.max(1, Math.round(cssH * dpr));
      if (canvas.width !== w || canvas.height !== h) {
        root.removeAttribute('data-calm-ready');
        if (poster) poster.hidden = false;
        canvas.width = w; canvas.height = h; current = -1; return true;
      }
      return false;
    }

    var map = { s: 1, dx: 0, dy: 0, dw: frameW, dh: frameH }; // frame -> canvas mapping (cover)
    function drawBitmap(bm) {
      var cw = canvas.width, ch = canvas.height;
      var iw = bm.width || frameW, ih = bm.height || frameH;
      var s = Math.max(cw / iw, ch / ih);
      var dw = iw * s, dh = ih * s;
      map.s = s; map.dx = (cw - dw) / 2; map.dy = (ch < dh) ? 0 : (ch - dh) / 2; map.dw = dw; map.dh = dh; // wide viewports: keep the top (wall meter), crop the desk
      ctx.drawImage(bm, map.dx, map.dy, dw, dh);
    }

    // Key-press ripples: thin warm arcs spreading on the felt from each typed key, synced to the frame.
    var taps = [];
    var TAP_LEN = (cfg.tapFrames || 11);
    if (cfg.tapsUrl) {
      fetch(cfg.tapsUrl, fetchOpts).then(function (r) { return r.ok ? r.json() : []; }).then(function (arr) {
        if (destroyed) return;
        taps = (arr || []).map(function (t) { var p = isMobile ? t.sp : t.pc; return { f: t.f, x: p[0], y: p[1] }; });
        draw(true);
      }, function () {});
    }
    function tapsActive(frame) {
      for (var i = 0; i < taps.length; i++) { var a = frame - taps[i].f; if (a >= 0 && a < TAP_LEN) return true; }
      return false;
    }
    function drawTaps(frame) {
      if (!taps.length) return;
      var base = map.dw * (isMobile ? 0.05 : 0.027); // ripple radius in canvas px
      ctx.save();
      ctx.lineCap = 'round';
      for (var i = 0; i < taps.length; i++) {
        var t = taps[i], age = frame - t.f;
        if (age < 0 || age >= TAP_LEN) continue;
        var u = age / TAP_LEN, e = 1 - Math.pow(1 - u, 2.2); // ease-out expansion
        var cx = map.dx + t.x * map.dw, cy = map.dy + t.y * map.dh;
        for (var k = 0; k < 2; k++) { // two rings, the outer one born first
          var uk = e - k * 0.3; if (uk <= 0) continue;
          var r = base * (0.3 + uk * 1.2), a = Math.pow(1 - uk, 1.5) * 0.8 * (1 - k * 0.3);
          if (a <= 0.01) continue;
          ctx.beginPath();
          ctx.ellipse(cx, cy, r, r * 0.42, 0, 0, Math.PI * 2);
          ctx.strokeStyle = 'rgba(255,238,214,' + a.toFixed(3) + ')';
          ctx.lineWidth = Math.max(1, map.dw * 0.0011 * (1 - uk * 0.5));
          ctx.shadowColor = 'rgba(255,225,190,' + (a * 0.6).toFixed(3) + ')';
          ctx.shadowBlur = map.dw * 0.004;
          ctx.stroke();
        }
      }
      ctx.restore();
    }

    function nearestBitmap(i) {
      if (bitmaps[i]) return i;
      for (var d = 1; d < total; d++) {
        if (i - d >= 0 && bitmaps[i - d]) return i - d;
        if (i + d < total && bitmaps[i + d]) return i + d;
      }
      return -1;
    }

    function release(bm) { if (bm && bm.close) bm.close(); }

    function decodeViaImage(blob) { // fallback for browsers without createImageBitmap
      return new Promise(function (res, rej) {
        var u = URL.createObjectURL(blob), im = new Image();
        im.onload = function () { URL.revokeObjectURL(u); res(im); };
        im.onerror = function () { URL.revokeObjectURL(u); rej(); };
        im.src = u;
      });
    }

    function decode(k) {
      if (destroyed || document.hidden || pendingDecodes >= 2 || bitmaps[k] || decoding[k] || !blobs[k]) return;
      decoding[k] = true; pendingDecodes++;
      var p = canBitmap ? createImageBitmap(blobs[k]) : decodeViaImage(blobs[k]);
      p.then(function (bm) {
        decoding[k] = false; pendingDecodes--;
        if (destroyed || document.hidden) { release(bm); return; }
        if (Math.abs(k - Math.round(playhead.frame)) > WINDOW) { release(bm); draw(); return; }
        bitmaps[k] = bm; draw();
      }, function () {
        decoding[k] = false; pendingDecodes--;
        if (!destroyed && !document.hidden && nearestBitmap(Math.round(playhead.frame)) < 0) fallback('frame-decode');
      });
    }

    var winLo = 0, winHi = -1; // last window we managed, so a draw only touches what changed
    function manageWindow(center) {
      var lo = Math.max(0, center - WINDOW), hi = Math.min(total - 1, center + WINDOW);
      if (lo === winLo && hi === winHi) { for (var d = lo; d <= hi; d++) if (!bitmaps[d] && blobs[d]) decode(d); return; }
      for (var k = winLo; k <= winHi; k++) if ((k < lo || k > hi) && bitmaps[k]) { release(bitmaps[k]); bitmaps[k] = null; }
      for (var j = lo; j <= hi; j++) decode(j);
      winLo = lo; winHi = hi;
    }

    // Impact meter: "direct on desk" vs "Calm" level stacks, drawn on the wall BEHIND the product.
    // The wall rectangle is projected per frame in Blender (calm-wall.json) and the product silhouette
    // (calm_mask_*.webp, alpha = product) punches the meter out so the keyboard and Calm stay in front.
    var meterCfg = cfg.meter && cfg.meter.show !== false ? cfg.meter : null;
    var METER_ATTACK = 1.5, METER_DECAY = (cfg.meterDecay || 9);
    var METER_N = 16;
    var meterStart = (meterCfg && meterCfg.start != null) ? meterCfg.start : 146;
    var wallQuads = null;                       // frame index -> [[x,y] TL, TR, BR, BL] (frame-normalized)
    var maskStart = meterCfg && meterCfg.maskStart != null ? meterCfg.maskStart : 145;
    var maskEnd = meterCfg && meterCfg.maskEnd != null ? meterCfg.maskEnd : 199;
    var maskBlobs = {}, maskBitmaps = {}, maskDecoding = {}, MASK_WIN = 3;
    var ov = null, ovCtx = null;                // offscreen layer for the meter
    function maskUrl(i) {
      var pre = isMobile ? (meterCfg.maskPrefixMobile || meterCfg.maskPrefix) : meterCfg.maskPrefix;
      if (!pre) return null;
      var n = ('0000' + (i + 1)).slice(-4);
      var at = pre.lastIndexOf('0000');
      return at >= 0 ? pre.slice(0, at) + n + pre.slice(at + 4) : pre + n + '.' + (meterCfg.maskExt || 'webp');
    }
    function loadMasks() {
      for (var i = maskStart; i <= maskEnd; i++) (function (k) {
        var u = maskUrl(k); if (!u) return;
        fetch(u, fetchOpts).then(function (r) { return r.ok ? r.blob() : null; }).then(function (bl) { if (bl && !destroyed) { maskBlobs[k] = bl; draw(true); } }, function () {});
      })(i);
    }
    function manageMasks(center) {
      for (var k = maskStart; k <= maskEnd; k++) {
        var inWin = Math.abs(k - center) <= MASK_WIN;
        if (inWin) {
          if (!maskBitmaps[k] && !maskDecoding[k] && maskBlobs[k]) {
            maskDecoding[k] = true;
            (function (kk) {
              var p = canBitmap ? createImageBitmap(maskBlobs[kk]) : decodeViaImage(maskBlobs[kk]);
              p.then(function (bm) { maskDecoding[kk] = false; if (destroyed || document.hidden || Math.abs(kk - Math.round(playhead.frame)) > MASK_WIN) { release(bm); return; } maskBitmaps[kk] = bm; draw(true); }, function () { maskDecoding[kk] = false; });
            })(k);
          }
        } else if (maskBitmaps[k]) { release(maskBitmaps[k]); delete maskBitmaps[k]; }
      }
    }
    function nearestMask(i) {
      if (maskBitmaps[i]) return maskBitmaps[i];
      for (var d = 1; d <= MASK_WIN; d++) { if (maskBitmaps[i - d]) return maskBitmaps[i - d]; if (maskBitmaps[i + d]) return maskBitmaps[i + d]; }
      return null;
    }
    if (meterCfg && meterCfg.wallUrl) {
      fetch(meterCfg.wallUrl, fetchOpts).then(function (r) { return r.ok ? r.json() : null; }).then(function (j) {
        if (destroyed || !j || !j.quads) return;
        wallQuads = {};
        Object.keys(j.quads).forEach(function (k) { wallQuads[k] = isMobile ? j.quads[k].sp : j.quads[k].pc; });
        loadMasks(); draw(true);
      }, function () {});
    }
    function meterLevel(frame) { // 0..1 envelope: fast attack, linear decay, per tap
      var lv = 0;
      for (var i = 0; i < taps.length; i++) {
        var a = frame - taps[i].f;
        if (a < 0 || a >= METER_DECAY) continue;
        var v = a < METER_ATTACK ? a / METER_ATTACK : 1 - (a - METER_ATTACK) / (METER_DECAY - METER_ATTACK);
        if (v > lv) lv = v;
      }
      return lv;
    }
    var peakCache = null; // peak hold per frame, built once: meterPeak was O(frames × taps) on every draw
    function meterPeak(frame) {
      if (!peakCache) {
        peakCache = {}; var m = 0;
        for (var f = meterStart; f <= maskEnd + 1; f++) { var v = meterLevel(f); if (v > m) m = v; peakCache[f] = m; }
      }
      var i = Math.round(frame);
      if (i < meterStart) return 0;
      return peakCache[i] != null ? peakCache[i] : peakCache[maskEnd + 1];
    }
    function meterAlpha(frame) { // fade in over 8 frames from meterStart, then hold
      if (!meterCfg || !taps.length || !wallQuads) return 0;
      return Math.max(0, Math.min(1, (frame - meterStart) / 8));
    }
    function quadAt(frame) {
      var i = Math.round(frame), q = wallQuads[i];
      if (!q) { var ks = Object.keys(wallQuads); q = wallQuads[i < +ks[0] ? ks[0] : ks[ks.length - 1]]; }
      return q.map(function (p) { return [map.dx + p[0] * map.dw, map.dy + p[1] * map.dh]; });
    }
    function drawMeter(frame) {
      var fa = meterAlpha(frame);
      if (fa <= 0) return;
      var cw = canvas.width, ch = canvas.height;
      if (!ov) { ov = document.createElement('canvas'); ovCtx = ov.getContext('2d'); }
      if (!ovCtx) return;
      if (ov.width !== cw || ov.height !== ch) { ov.width = cw; ov.height = ch; }
      var g = ovCtx; g.setTransform(1, 0, 0, 1, 0, 0); g.clearRect(0, 0, cw, ch);
      var q = quadAt(frame), TL = q[0], TR = q[1], BR = q[2], BL = q[3];
      // keep the meter inside the visible canvas: on wide viewports the cover crop cuts the top of the
      // frame, so lower the top edge of the wall rectangle (bottom edge stays on the wall) when needed
      var fontPx = Math.max(10, Math.sqrt(Math.pow(TR[0] - TL[0], 2) + Math.pow(TR[1] - TL[1], 2)) * 0.026);
      var minTop = ch * 0.05 + fontPx * 1.8;
      function clampTop(T, B) { if (T[1] >= minTop) return T; var t = (minTop - B[1]) / (T[1] - B[1]); return [B[0] + (T[0] - B[0]) * t, minTop]; }
      TL = clampTop(TL, BL); TR = clampTop(TR, BR);
      // affine map: unit square (u right, v down) -> wall parallelogram
      var ax = TR[0] - TL[0], ay = TR[1] - TL[1], bx = BL[0] - TL[0], by = BL[1] - TL[1];
      g.setTransform(ax, ay, bx, by, TL[0], TL[1]);
      // horizontal level bars (audio-meter style), one row per condition, filling left -> right,
      // with a peak-hold tick that stays at the furthest segment reached so far
      var lv = meterLevel(frame), n = 28;
      var rows = [
        { v0: 0.30, v1: 0.42, scale: 1.0, label: meterCfg.labelDirect || '机に直置き', calm: false },
        { v0: 0.74, v1: 0.86, scale: 0.18, label: meterCfg.labelCalm || 'Calm', calm: true }
      ];
      var pitch = 1 / n, segW = pitch * 0.62;
      for (var r = 0; r < rows.length; r++) {
        var R = rows[r];
        var lit = Math.round(lv * R.scale * n);
        if (R.calm && lv > 0.05 && lit < 1) lit = 1;
        var pk = Math.round(meterPeak(frame) * R.scale * n); if (R.calm && pk < 1 && meterPeak(frame) > 0.05) pk = 1;
        for (var k = 0; k < n; k++) {
          var on = k < lit, hot = on && k === lit - 1 && lit > n * 0.6;
          g.fillStyle = hot ? 'rgba(178,104,52,0.95)' : (on ? 'rgba(40,33,28,0.78)' : 'rgba(40,33,28,0.13)');
          g.fillRect(k * pitch, R.v0, segW, R.v1 - R.v0);
        }
        if (pk > 0) { // peak-hold tick
          var px = pk * pitch - (pitch - segW) * 0.5;
          g.fillStyle = R.calm ? 'rgba(40,33,28,0.9)' : 'rgba(178,104,52,0.95)';
          g.fillRect(px - pitch * 0.06, R.v0 - 0.04, pitch * 0.12, (R.v1 - R.v0) + 0.08);
        }
      }
      // labels above each row, note below (canvas space, not sheared, so text stays crisp)
      g.setTransform(1, 0, 0, 1, 0, 0);
      g.fillStyle = 'rgba(40,33,28,0.88)';
      g.font = '500 ' + fontPx + 'px -apple-system, "Helvetica Neue", Arial, "Hiragino Sans", sans-serif';
      g.textBaseline = 'alphabetic';
      for (var r2 = 0; r2 < rows.length; r2++) {
        var v = rows[r2].v0;
        g.fillText(rows[r2].label, TL[0] + bx * v, TL[1] + by * v - fontPx * 0.55);
      }
      if (meterCfg.note) {
        g.fillStyle = 'rgba(40,33,28,0.55)';
        g.font = '400 ' + (fontPx * 0.78) + 'px -apple-system, "Helvetica Neue", Arial, "Hiragino Sans", sans-serif';
        g.fillText(meterCfg.note, TL[0] + bx * 1.0, TL[1] + by * 1.0 + fontPx * 1.2);
      }
      // punch out the product silhouette so the meter sits behind keyboard + Calm
      var mk = nearestMask(Math.round(frame));
      if (mk) {
        g.globalCompositeOperation = 'destination-out';
        g.drawImage(mk, map.dx, map.dy, map.dw, map.dh);
        g.globalCompositeOperation = 'source-over';
      }
      ctx.save(); ctx.globalAlpha = fa; ctx.drawImage(ov, 0, 0); ctx.restore();
    }

    var lastDrawn = -1;
    function draw(force) {
      if (destroyed || document.hidden) return;
      ensureSize();
      var i = Math.round(playhead.frame);
      manageWindow(i);
      if (meterCfg) manageMasks(i);
      var j = nearestBitmap(i);
      if (j < 0) return;
      var key = Math.round(playhead.frame * 4) / 4; // redraw on quarter-frame steps so overlays animate smoothly
      var overlay = tapsActive(playhead.frame) || meterAlpha(playhead.frame) > 0;
      if (j === current && !force && !(overlay && key !== lastDrawn)) return;
      try {
        drawBitmap(bitmaps[j]);
        drawMeter(playhead.frame);
        if (tapsActive(playhead.frame)) drawTaps(playhead.frame);
      } catch (e) { fallback('canvas-draw'); return; }
      current = j; lastDrawn = key;
      root.setAttribute('data-calm-ready', 'true');
      if (poster && !poster.hidden) poster.hidden = true;
    }

    // progressive fetch: first, last, then sweep; 4 in flight
    var order = [0, total - 1];
    for (var i = 1; i < total - 1; i++) order.push(i);
    var cursor = 0, inflight = 0, MAX = 4;
    function fetchOne(k, cb) {
      fetch(urls[k], fetchOpts).then(function (r) { return r.ok ? r.blob() : null; }).then(function (b) {
        if (destroyed) return;
        if (b) blobs[k] = b;
        else if (k === 0) { fallback('frame-load'); return; }
        cb();
      }, function () { if (!destroyed) { if (k === 0) fallback('frame-load'); else cb(); } });
    }
    function pump() {
      while (!destroyed && !document.hidden && inflight < MAX && cursor < order.length) {
        inflight++;
        fetchOne(order[cursor++], function () { inflight--; draw(); pump(); });
      }
    }

    var resizeObserver = null;
    function onResize(entries) {
      if (destroyed || document.hidden) return;
      var nextDpr = Math.min(window.devicePixelRatio || 1, 2);
      if (nextDpr !== dpr) { dpr = nextDpr; current = -1; }
      if (entries && entries[0] && entries[0].contentRect) { cssW = entries[0].contentRect.width; cssH = entries[0].contentRect.height; }
      else measure();
      if (ensureSize()) draw(true);
    }
    measure(); ensureSize();
    window.addEventListener('resize', onResize, { passive: true });
    if (window.ResizeObserver) { resizeObserver = new ResizeObserver(onResize); resizeObserver.observe(canvas); }
    // screen readers: only the visible copy stage is exposed
    if (stage0El) stage0El.style.opacity = 1; // visible before the first scroll update
    if (taglineEl) taglineEl.style.opacity = 1;
    function setStage(idx) {
      stageEls.forEach(function (el, i) { var on = i === idx; el.setAttribute('aria-hidden', on ? 'false' : 'true'); if ('inert' in el) el.inert = !on; });
      if (stage) stage.setAttribute('data-calm-stage', String(idx));
    }
    setStage(0);

    function syncCopy(p) {
      if (destroyed) return;
      root.style.setProperty('--calm-progress', p.toFixed(4));
      var hold = Math.max(0, Math.min(1, (p - seqEnd) / Math.max(0.001, 1 - seqEnd)));
      root.style.setProperty('--calm-hold', hold.toFixed(4));
      var open = Math.max(0, Math.min(1, (0.17 - p) / 0.055)); // opening copy clears before the case arrives
      if (stage0El) { stage0El.style.opacity = open; stage0El.style.pointerEvents = open < 0.05 ? 'none' : ''; }
      if (taglineEl) taglineEl.style.opacity = Math.max(0, Math.min(1, (0.13 - p) / 0.05));
      var s = 0, ranges = cfg.stages || [];
      for (var k = 0; k < ranges.length; k++) if (p >= ranges[k]) s = k;
      if (stage && stage.getAttribute('data-calm-stage') !== String(s)) setStage(s);
    }

    var seqEnd = (cfg.seqEnd != null) ? cfg.seqEnd : 0.88; // fraction of the track used by the sequence; the rest holds the final frame
    var tl = window.gsap.timeline({
      scrollTrigger: {
        trigger: root, start: 'top top', end: 'bottom bottom',
        scrub: cfg.scrub == null ? 0.25 : cfg.scrub,
        onUpdate: function (st) { syncCopy(st.progress); }
      }
    });
    tl.to(playhead, { frame: total - 1, ease: 'none', duration: seqEnd, onUpdate: function () { draw(); } }, 0);
    tl.to({}, { duration: 1 - seqEnd }, seqEnd); // hold on the last frame

    function releaseFrames() {
      for (var i = 0; i < bitmaps.length; i++) if (bitmaps[i]) { release(bitmaps[i]); bitmaps[i] = null; }
      Object.keys(maskBitmaps).forEach(function (k) { release(maskBitmaps[k]); delete maskBitmaps[k]; });
      winLo = 0; winHi = -1; current = -1;
    }
    function onContextLost() { fallback('canvas-context-lost'); }
    function resume() {
      if (destroyed || document.hidden) return;
      onResize();
      if (tl.scrollTrigger) {
        tl.scrollTrigger.refresh(); tl.scrollTrigger.update();
        // A frozen/background tab may miss the tween's final tick.
        tl.progress(tl.scrollTrigger.progress);
        syncCopy(tl.scrollTrigger.progress);
      }
      draw(true); pump();
    }
    function onVisibility() { if (document.hidden) releaseFrames(); else resume(); }
    cleanupSequence = function () {
      if (destroyed) return;
      destroyed = true;
      if (controller) controller.abort();
      window.removeEventListener('resize', onResize);
      window.removeEventListener('pageshow', resume);
      document.removeEventListener('visibilitychange', onVisibility);
      document.removeEventListener('resume', resume);
      canvas.removeEventListener('contextlost', onContextLost);
      if (resizeObserver) resizeObserver.disconnect();
      if (tl.scrollTrigger) tl.scrollTrigger.kill();
      tl.kill(); releaseFrames();
      blobs.length = 0; maskBlobs = {};
      canvas.width = 1; canvas.height = 1;
      if (ov) { ov.width = 1; ov.height = 1; }
    };
    canvas.addEventListener('contextlost', onContextLost);
    document.addEventListener('visibilitychange', onVisibility);
    document.addEventListener('resume', resume);
    window.addEventListener('pageshow', resume);
    root.setAttribute('data-calm-mode', 'sequence');
    root._calm.tween = tl;
    resume();

  }

  function boot() {
    document.querySelectorAll('[data-calm-hero]').forEach(function (el) {
      if (!el._calmInit) { el._calmInit = true; init(el); }
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('shopify:section:unload', function (e) {
    var t = e.target;
    var root = t && t.matches && t.matches('[data-calm-hero]') ? t : (t && t.querySelector ? t.querySelector('[data-calm-hero]') : null);
    if (root && root._calm && root._calm.destroy) root._calm.destroy();
  });
})();
